"use client";

import { useRouter, usePathname } from "next/navigation";
import { useCallback, useMemo } from "react";
import {
  getLocaleFromPath,
  addLocaleToPath,
  type SupportedLocale,
} from "@/app/lib/i18n-config";

interface LocalizedNavigateOptions {
  scroll?: boolean;
  locale?: SupportedLocale;
}

// Paths that should NOT have locale prefixes added
// These routes have content determined by the page itself, not user preference
const EXCLUDED_PATHS = ["/blog", "/job"];

/**
 * Check if a path should be excluded from locale prefixing.
 */
function isExcludedPath(path: string): boolean {
  return EXCLUDED_PATHS.some(
    (excluded) => path === excluded || path.startsWith(`${excluded}/`)
  );
}

/**
 * A hook that wraps Next.js useRouter with locale-aware navigation.
 *
 * Provides:
 * - `locale`: The current locale from the pathname
 * - `push`: Navigate to a path with automatic locale prefix
 * - `replace`: Replace current path with automatic locale prefix
 * - `back`: Navigate back in history
 * - `forward`: Navigate forward in history
 * - `refresh`: Refresh the current page
 *
 * Note: /blog and /job paths are excluded from locale prefixing (content is single-language)
 */
export function useLocalizedRouter() {
  const router = useRouter();
  const pathname = usePathname();

  const locale = useMemo(() => getLocaleFromPath(pathname), [pathname]);

  const push = useCallback(
    (path: string, options?: LocalizedNavigateOptions) => {
      // Query-only paths (e.g., "?tab=creatives") should not be localized
      // They update query params on the current page
      if (path.startsWith("?")) {
        router.push(path, { scroll: options?.scroll });
        return;
      }
      // Skip localization for excluded paths
      if (isExcludedPath(path)) {
        router.push(path, { scroll: options?.scroll });
        return;
      }
      const targetLocale = options?.locale ?? locale;
      const localizedPath = addLocaleToPath(path, targetLocale);
      router.push(localizedPath, { scroll: options?.scroll });
    },
    [router, locale]
  );

  const replace = useCallback(
    (path: string, options?: LocalizedNavigateOptions) => {
      // Query-only paths (e.g., "?tab=creatives") should not be localized
      // They update query params on the current page
      if (path.startsWith("?")) {
        router.replace(path, { scroll: options?.scroll });
        return;
      }
      // Skip localization for excluded paths
      if (isExcludedPath(path)) {
        router.replace(path, { scroll: options?.scroll });
        return;
      }
      const targetLocale = options?.locale ?? locale;
      const localizedPath = addLocaleToPath(path, targetLocale);
      router.replace(localizedPath, { scroll: options?.scroll });
    },
    [router, locale]
  );

  const back = useCallback(() => {
    router.back();
  }, [router]);

  const forward = useCallback(() => {
    router.forward();
  }, [router]);

  const refresh = useCallback(() => {
    router.refresh();
  }, [router]);

  return {
    locale,
    push,
    replace,
    back,
    forward,
    refresh,
  };
}
